import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import colors from "../../Constants/colors";

import { updateFirstTimeUser } from "../../features/auth/authSlice";
import { useObserveElement } from "./useObserveElement";

const Onboarding = ({
  run,
  steps,
  setSteps,
  setRun,
  stepIndex,
  setStepIndex,
  selectedOption,
  setSelectedOption,
  user_id,
}) => {
  const organization = useSelector((state) => state.organization);

  // Define the keyframes for the pulse animation
  const pulseAnimation = keyframes`
0% {
    transform: scale(1);
}
55% {
    background-color: var(--secondaryBlue);
    transform: scale(1.6);
}
`;

  const dispatch = useDispatch();

  const option1bVisible = useObserveElement('[data-tour="option-1b"]');

  const option2aVisible = useObserveElement('[data-tour="option-2a"]');

  const option1gVisible = useObserveElement('[data-tour="option-1g"]');

  const option1iVisible = useObserveElement('[data-tour="option-1i"]');

  const option2cVisible = useObserveElement('[data-tour="option-2c"]');

  const option2eVisible = useObserveElement('[data-tour="option-2e"]');

  // Mapping step targets to their visibility states
  const targetVisibilityMap = {
    '[data-tour="option-1a"]': option1bVisible,
    '[data-tour="option-3"]': option2aVisible,
    '[data-tour="option-1f"]': option1gVisible,
    '[data-tour="option-1h"]': option1iVisible,
    '[data-tour="option-2b"]': option2cVisible,
    '[data-tour="option-2c"]': option2eVisible,
  };

  // Beacon component using MUI's sx prop and displaying "Start Tour"
  const BeaconComponent = (props) => {
    return (
      <Box
        sx={{
          animation: `${pulseAnimation} 1s ease-in-out infinite`,
          backgroundColor: colors.tertiaryBlue, // Use primaryBlue for background
          borderRadius: "50%",
          display: "flex", // Center the text
          justifyContent: "center",
          alignItems: "center",
          height: "1rem",
          width: "1rem",
          color: "white", // Text color
          fontWeight: "bold", // Bold text
          fontSize: "0.1rem",
        }}
        {...props}
      ></Box>
    );
  };

  const handleOptionSelect = (_event, option) => {
    if (!option) return; // If the selection is cleared or invalid

    let nextSteps;
    if (option === "Option 1") {
      nextSteps = [
        ...steps.filter((step) => !step.target.includes("option-")), // Filter out any previously added option steps
        {
          target: '[data-tour="option-1a"]',
          id: "un_1a",
          placement: "right",
          content: (
            <div>
              <p>
                Retrieval helps find relevant data for your queries by
                configuring chunk size, metric type, retrieval methods, and
                embedding models.{" "}
              </p>
              <p>
                Drag and drop the Retrieval node from sidebar into the workspace
                to continue.
              </p>
            </div>
          ),
          options: {
            nextButtonName: "Continue →",
            showDatasetMedia: "drag_retrieval",
          },
          spotlightClicks: true,
        },
      ];
    } else if (option === "Option 2") {
      nextSteps = [
        ...steps.filter((step) => !step.target.includes("option-")), // Filter out any previously added option steps
        {
          target: '[data-tour="option-1a"]',
          id: "un_1b",
          placement: "right",
          content: (
            <div>
              <p>
                Retrieval helps find relevant data for your queries by
                configuring chunk size, metric type, retrieval methods, and
                embedding models.
              </p>
              <p>
                Drag and drop the Retrieval node from sidebar into the workspace
                to continue.
              </p>
            </div>
          ),
          options: {
            nextButtonName: "Continue →",
            showDatasetMedia: "drag_retrieval",
          },
          spotlightClicks: true,
        },
      ];
    } else {
      nextSteps = [
        ...steps.filter((step) => !step.target.includes("option-")), // Filter out any previously added option steps
        {
          target: '[data-tour="option-3"]',
          id: "un_1c",
          placement: "right",
          content: (
            <div>
              <p>
                Generation helps customize how the LLM creates responses by
                configuring models, prompt settings, and output parameters.
              </p>
              <p>Drag the Generation node to proceed.</p>
            </div>
          ),
          options: {
            nextButtonName: "Continue →",
            showDatasetMedia: "drag_generation",
          },
          spotlightClicks: true,
        },
      ];
    }

    setSteps(nextSteps); // Update the steps with the user's selection
    setSelectedOption(option); // Set the selected option in state
  };

  // Render custom tooltip with Back, Skip, and Next buttons
  const renderCustomTooltip = ({
    step,
    backProps,
    primaryProps,
    skipProps,
    continuous,
  }) => {
    const isTargetVisible =
      step.target in targetVisibilityMap
        ? targetVisibilityMap[step.target]
        : true;
    return (
      <Box
        sx={{
          p: 4,
          backgroundColor: "white",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          maxWidth: "450px",
          fontSize: "0.7rem",
          bgcolor: colors.extremeBlue,
          color: colors.extremeWhite,
          borderRadius: "12px",
        }}
      >
        <Typography variant="body2">{step.content}</Typography>

        {/* If the step includes options (e.g., step 3), show the buttons for option selection */}
        {step.options && step.options.showButtons && (
          <Box mt={2}>
            <ToggleButtonGroup
              value={selectedOption}
              exclusive
              onChange={handleOptionSelect}
              aria-label="Option Selection"
            >
              <ToggleButton
                value="Option 1"
                aria-label="Retrieval Only"
                sx={{
                  fontSize: "0.7rem",
                  bgcolor: colors.primaryBlue,
                  color: colors.extremeWhite,
                  mr: 0.5,
                  "&.Mui-selected": {
                    fontWeight: "bold",
                    bgcolor: colors.tertiaryBlue, // Keeps the background color on selection
                    color: colors.extremeWhite, // Ensures the text stays white
                    "&:hover": {
                      fontWeight: "bold", // Changes background on hover if you need
                      color: colors.extremeWhite,
                      bgcolor: colors.secondaryBlue,
                    },
                  },
                  "&:hover": {
                    fontWeight: "bold", // Changes background on hover if you need
                    color: colors.extremeWhite,
                    bgcolor: colors.secondaryBlue,
                  },
                }}
              >
                Retrieval Only
              </ToggleButton>
              <ToggleButton
                value="Option 2"
                aria-label="Retrieval and Generation"
                sx={{
                  fontSize: "0.7rem",
                  bgcolor: colors.primaryBlue,
                  color: colors.extremeWhite,
                  mr: 0.5,
                  "&.Mui-selected": {
                    fontWeight: "bold",
                    bgcolor: colors.tertiaryBlue, // Keeps the background color on selection
                    color: colors.extremeWhite, // Ensures the text stays white
                    "&:hover": {
                      fontWeight: "bold", // Changes background on hover if you need
                      color: colors.extremeWhite,
                      bgcolor: colors.secondaryBlue,
                    },
                  },
                  "&:hover": {
                    fontWeight: "bold", // Changes background on hover if you need
                    color: colors.extremeWhite,
                    bgcolor: colors.secondaryBlue,
                  },
                }}
              >
                Retrieval Augmented Generation
              </ToggleButton>
              <ToggleButton
                value="Option 3"
                aria-label="Generation Only"
                sx={{
                  fontSize: "0.7rem",
                  bgcolor: colors.primaryBlue,
                  color: colors.extremeWhite,
                  "&.Mui-selected": {
                    fontWeight: "bold",
                    bgcolor: colors.tertiaryBlue, // Keeps the background color on selection
                    color: colors.extremeWhite, // Ensures the text stays white
                    "&:hover": {
                      fontWeight: "bold", // Changes background on hover if you need
                      color: colors.extremeWhite,
                      bgcolor: colors.secondaryBlue,
                    },
                  },
                  "&:hover": {
                    fontWeight: "bold", // Changes background on hover if you need
                    color: colors.extremeWhite,
                    bgcolor: colors.secondaryBlue,
                  },
                }}
              >
                Generation Only
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {step.options && step.options.showDatasetMedia && (
          <Box sx={{ p: 2 }}>
            {step.options.showDatasetMedia === "drag_generation" && (
              <img
                src="/drag_generation_crop.gif"
                alt="Drag Generation"
                style={{ width: "100%", height: "auto" }} // optional styling
              />
            )}
            {step.options.showDatasetMedia === "drag_retrieval" && (
              <img
                src="/drag_retrieval_crop.gif"
                alt="Drag Retrieval"
                style={{ width: "100%", height: "auto" }} // optional styling
              />
            )}
            {step.options.showDatasetMedia === "show_connections" && (
              <img
                src="/show_connections_crop.gif"
                alt="Drag Retrieval"
                style={{ width: "100%", height: "auto" }} // optional styling
              />
            )}
          </Box>
        )}

        {/* Back, Skip, and Next buttons */}
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {skipProps && (
            <Button
              sx={{
                fontSize: "0.7rem",
                color: colors.extremeWhite,
                border: `1px solid ${colors.extremeWhite}`,
                padding: "8px 12px",
              }}
              variant="text"
              {...skipProps}
            >
              {skipProps.title}
            </Button>
          )}

          <Box sx={{ fontSize: "0.7rem" }} display="flex" gap={2}>
            {backProps && stepIndex > 0 && (
              <Button
                sx={{
                  fontSize: "0.7rem",
                  bgcolor: colors.secondaryBlue,
                  color: colors.extremeWhite,
                  padding: "8px 12px",
                  "&:hover": {
                    bgcolor: colors.tertiaryBlue, // Slightly darker on hover
                  },
                }}
                variant="outlined"
                {...backProps}
              >
                {backProps.title}
              </Button>
            )}
            {(!step.options?.showButtons || selectedOption !== null) &&
              continuous &&
              isTargetVisible && ( // Only show button if target is visible
                <Button
                  sx={{
                    fontSize: "0.7rem",
                    bgcolor: colors.primaryBlue,
                    padding: "8px 12px",
                    "&:hover": {
                      bgcolor: colors.secondaryBlue,
                    },
                  }}
                  variant="contained"
                  {...primaryProps}
                >
                  {step.options?.nextButtonName
                    ? step.options?.nextButtonName
                    : primaryProps.title || "Next"}
                </Button>
              )}
          </Box>
        </Box>
      </Box>
    );
  };

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    // Update stepIndex based on user action
    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false); // Stop the tour
      setStepIndex(0);
      if (organization?.organization?.bot?.length > 0) {
        dispatch(updateFirstTimeUser({ user_id, status: false }));
      }
    }

    console.groupCollapsed(type);
    console.groupEnd();
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      scrollToFirstStep={false}
      beaconComponent={BeaconComponent}
      disableScrolling={true} // Prevents Joyride from scrolling the window
      disableScrollParentFix={true}
      showSkipButton
      //   disableOverlayClose
      stepIndex={stepIndex} // Control the current step index
      callback={handleJoyrideCallback}
      spotlightPadding={0} // Adds padding around the highlighted element
      floaterProps={{
        placement: stepIndex === 7 ? "center" : "auto", // You can specify 'top', 'bottom', etc., or use 'auto' to let it adjust
        disableAnimation: true,
      }}
      styles={{
        tooltipContainer:
          stepIndex === 7
            ? {
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10000,
                width: "400px", // Adjust width as necessary
              }
            : { zIndex: 10000, top: "10%", left: "10%", position: "relative" },
        tooltip: {
          backgroundColor: "white",
          borderRadius: "8px",
        },
        options: {
          zIndex: 10000,
        }, // Use default styles for other steps
      }}
      tooltipComponent={renderCustomTooltip} // Custom tooltip rendering
    />
  );
};

export default Onboarding;
